import type { EntityPluginContext, JobHandler } from "@brains/plugins";
import {
  parseMarkdownWithFrontmatter,
  generateMarkdownWithFrontmatter,
} from "@brains/plugins";
import { fetchStyleGuide, formatVoiceGuidance } from "@brains/contracts";
import type { Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import { computeContentHash } from "@brains/utils/hash";
import {
  seriesSchema,
  seriesFrontmatterSchema,
  createSeriesBodyFormatter,
} from "../schemas/series";

const seriesGenerationJobSchema: z.ZodObject<{
  prompt: z.ZodOptional<z.ZodString>;
  title: z.ZodOptional<z.ZodString>;
  seriesId: z.ZodOptional<z.ZodString>;
}> = z.object({
  prompt: z.string().optional(),
  title: z.string().optional(),
  seriesId: z.string().optional(),
});

type SeriesGenerationJobData = z.output<typeof seriesGenerationJobSchema>;

/** Shape the series description template returns. */
export const generatedSeriesDescriptionSchema: z.ZodObject<{
  description: z.ZodString;
}> = z.object({ description: z.string() });

/** Member fields used to build the description prompt. */
const memberSummarySchema: z.ZodObject<{
  title: z.ZodOptional<z.ZodString>;
  excerpt: z.ZodOptional<z.ZodString>;
}> = z.object({
  title: z.string().optional(),
  excerpt: z.string().optional(),
});

/**
 * Generation handler for series entities.
 * Generates AI descriptions from the series' member entities.
 */
export class SeriesGenerationHandler implements JobHandler<
  string,
  SeriesGenerationJobData
> {
  private readonly logger: Logger;
  private readonly context: EntityPluginContext;
  constructor(logger: Logger, context: EntityPluginContext) {
    this.logger = logger;
    this.context = context;
  }

  async process(data: SeriesGenerationJobData): Promise<unknown> {
    const seriesId = data.seriesId ?? data.title;
    if (!seriesId) {
      return { success: false, error: "seriesId or title required" };
    }

    const series = await this.context.entityService.getEntity(
      {
        entityType: "series",
        id: seriesId,
      },
      seriesSchema,
    );
    if (!series) {
      return { success: false, error: `Series not found: ${seriesId}` };
    }

    // Gather member content summaries across all entity types
    const summaries = await this.gatherMemberSummaries(series.metadata.title);
    if (summaries.length === 0) {
      return {
        success: false,
        error: `No members found in series: ${series.metadata.title}`,
      };
    }

    const prompt =
      data.prompt ??
      `Series name: ${series.metadata.title}\n\nContent in this series:\n${summaries.join("\n")}`;

    const voiceGuidance = formatVoiceGuidance(
      await fetchStyleGuide(this.context.entityService),
    );
    const generated = await this.context.ai.generate(
      {
        prompt,
        templateName: "series:description",
        representedIdentity: "none",
        ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
      },
      generatedSeriesDescriptionSchema,
    );

    if (!generated.description) {
      return { success: false, error: "Failed to generate description" };
    }

    // Update series entity with generated description
    const parsed = parseMarkdownWithFrontmatter(
      series.content,
      seriesFrontmatterSchema,
    );
    const formatter = createSeriesBodyFormatter(series.metadata.title);
    const newBody = formatter.format({ description: generated.description });
    const finalContent = generateMarkdownWithFrontmatter(
      newBody,
      parsed.metadata,
    );

    await this.context.entities.update({
      ...series,
      content: finalContent,
      contentHash: computeContentHash(finalContent),
      updated: new Date().toISOString(),
    });

    this.logger.info(
      `Enhanced series "${series.metadata.title}" with description`,
    );

    return {
      success: true,
      seriesId: series.id,
      seriesName: series.metadata.title,
      description: generated.description,
      memberCount: summaries.length,
    };
  }

  validateAndParse(data: unknown): SeriesGenerationJobData | null {
    const result = seriesGenerationJobSchema.safeParse(data);
    return result.success ? result.data : null;
  }

  private async gatherMemberSummaries(seriesName: string): Promise<string[]> {
    const summaries: string[] = [];
    const types = this.context.entityService.getEntityTypes();

    for (const type of types) {
      if (type === "series") continue;
      const entities = await this.context.entityService.listEntities({
        entityType: type,
        options: {
          filter: { metadata: { seriesName } },
          // Deliberate cap: these summaries feed an AI prompt, so bound the
          // context size rather than walk every member of a huge series.
          limit: 100,
        },
      });
      for (const entity of entities) {
        const parsed = memberSummarySchema.safeParse(entity.metadata);
        const { title, excerpt } = parsed.success ? parsed.data : {};
        summaries.push(`- "${title ?? entity.id}": ${excerpt ?? ""}`);
      }
    }

    return summaries;
  }
}
